import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { companyApi, companyQueryKeys } from "service/crm/api/company";
import { dealApi, dealQueryKeys } from "service/crm/api/deal";
import { NonAssociatedDealParams } from "types/crm/deal";

interface UseQueryOptions {
  enabled?: boolean;
}

/** Companies associated with a deal — GET /crm/deals/{dealUuid}/companies */
export const useDealCompanies = (dealUuid: string) => {
  return useQuery({
    queryKey: dealQueryKeys.companies(dealUuid),
    queryFn: () => dealApi.getDealCompanies(dealUuid),
    enabled: !!dealUuid,
  });
};


export const useNonAssociatedDealCompanies = (
  { dealUuid, search, limit }: Omit<NonAssociatedDealParams, "page">,
  options: UseQueryOptions = {},
) => {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: companyQueryKeys.nonAssociatedByDeal(dealUuid, {
      search,
      limit,
    }),
    queryFn: ({ pageParam }) =>
      companyApi.getNonAssociatedDealCompanies({
        dealUuid,
        search,
        limit,
        page: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.last ? undefined : lastPage.page + 1,
    enabled: (options.enabled ?? true) && !!dealUuid,
  });

  return {
    companies: data?.pages.flatMap((p) => p.content) ?? [],
    isLoading,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements: data?.pages[0]?.totalElements ?? 0,
  };
};
